import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import s from './Game.module.scss'
import PlayingField from './PlayingField/PlayingField'
import Control from './Control/Control'
import { useSearchParams } from 'react-router-dom'
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr'
import {ChangeResult, InitialState, SpinResult} from '../../types/Messages'
import InitialStateContext from '../../context/InitialStateContext'
import BalanceContext from '../../context/BalanceContext'
import GameConfigContext from '../../context/GameConfigContext'
import Api from '../../api'
import Popup from '../Popup/Popup'
import { useSounds } from '../../utils/useSounds'
import PopupsOpenContext, { IPopups } from '../../context/PopupsContext'
import ProvablyFairnessModal from './ProvablyFairnessPopup/ProvablyFairnessPopup'
import RulesPopup from './RulesPopup/RulesPopup'
import ErrorContext from '../../context/ErrorContext'
import OldHashAndKeyContext from '../../context/OldHashAndKeyContext'
import { useTranslation } from '../../hooks/useTranslation'
import {Result, RiskType, segmentColors, SegmentCountType} from '../../types/Game'
import {useRatchet} from "../../hooks/useRatchet";

export const AUTOBET_LIMIT_MIN = 0.000001
export const AUTOBET_LIMIT_MAX = 1000000000000000
export const AUTOBET_INCREASE_PERCENT_MIN = 0
export const AUTOBET_INCREASE_PERCENT_MAX = 99999

const Game = () => {
    const { t } = useTranslation()
    const [, setErr] = useContext(ErrorContext)

    const { betSound, winSound, autoplaySound, crashSound, clickSound} = useSounds()

    const [params] = useSearchParams()
    const partnerId = params.get('PartnerId')
    const isDrizzlePartner = useMemo(() => partnerId === process.env.REACT_APP_DRIZZLE_PARTNER_ID, [partnerId])
    const gameName = params.get('GameName')
    const oneTimeToken = params.get('OneTimeToken')
    const mode = params.get('Mode')

    const [connection, setConnection] = useState<HubConnection | null>(null)

    const [balance, setBalance] = useContext(BalanceContext)
    const [initialState, setInitialState] = useContext(InitialStateContext)

    const [{ isMute, isTurboMode }] = useContext(GameConfigContext)
    const [popupOpen, setPopupOpen] = useContext(PopupsOpenContext)

    const openPopup = useCallback(
        (popup: keyof IPopups, value: boolean) => {
            setPopupOpen(prev => ({
                ...prev,
                [popup]: value,
            }))
        },
        [popupOpen]
    )

    const [oldHashAndKey, setOldHashAndKey] = useContext(OldHashAndKeyContext)

    // Control
    const [amount, setAmount] = useState<string>('0')
    const [riskType, setRiskType] = useState<RiskType>('low')
    const [segmentsCount, setSegmentsCount] = useState<SegmentCountType>('_10')

    useEffect(() => {
        if (!initialState) return

        changeResult()
    }, [initialState, riskType, segmentsCount])

    const [isAutobet, setIsAutobet] = useState(false)
    const [increaseOnWin, setIncreaseOnWin] = useState('0')
    const [increaseOnLoss, setIncreaseOnLoss] = useState('0')
    const [stopAutobetOnProfit, setStopAutobetOnProfit] = useState('0')
    const [stopAutobetOnLoss, setStopAutobetOnLoss] = useState('0')
    const [isAutobetRunning, setIsAutobetRunning] = useState(false)

    const lastGameResult = useRef<'lose' | 'win' | null>(null)
    const amountAutobetResult = useRef(0)

    useEffect(() => {
        lastGameResult.current = null
        amountAutobetResult.current = 0

        if (isAutobet) {
            autoplaySound()
        } else {
            setIsAutobetRunning(false)
        }
    }, [isAutobet])

    const [numberOfRounds, setNumberOfRounds] = useState('10')

    const [isGame, setIsGame] = useState(false)
    const [gameResult, setGameResult] = useState<Result | null>(null)
    const { setIsRatchet } = useRatchet()

    const handleInitialState = (data: InitialState) => {
        console.log('InitialState', data)
        setBalance(data.balance)
        setInitialState(data)
    }

    const gameEndHandler = (data: SpinResult, odd: number) => {
        setIsRatchet(false)

        setOldHashAndKey({
            ...data.oldResultHashAndKey,
            odd,
        })

        if (data.spinStatus === 0) {
            if (isAutobet) {
                lastGameResult.current = 'lose'
                amountAutobetResult.current = amountAutobetResult.current - +amount
            }

            crashSound()
            setBalance(data.balance)
        }

        if (data.spinStatus === 1) {
            if (isAutobet) {
                lastGameResult.current = 'win'
                amountAutobetResult.current = amountAutobetResult.current + data.winAmount
            }

            winSound()
            setBalance(data.balance + data.winAmount)
        }

        setTimeout(() => setIsGame(false), 1000) // время показа результат
    }

    const handleSpinResult = (data: SpinResult) => {
        console.log('SpinResult', data)

        if (data.responseCode > 0) {
            setErr(t('UNEXPECTEDERR'))
            setIsGame(false)
            return
        }

        try {
            const color = segmentColors[data.result.color]
            const odd =  initialState.oddsInfo[riskType][segmentsCount][color]!

            setGameResult({ color, odd })

            if (isTurboMode) {
                gameEndHandler(data, odd)
            } else {
                setIsRatchet(true)
                setTimeout(() => gameEndHandler(data, odd), 3000) // время анимации 3s
            }
        } catch {
            setErr(t('UNEXPECTEDERR'))
            setIsGame(false)
        }
    }

    const handleChangeResult = (data: ChangeResult) => {
        console.log('ChangeResult', data)
    }

    const runAutoBet = async () => {
        if (!isAutobet || +numberOfRounds < 1) return

        let currentAmount = +amount

        if (currentAmount > balance) {
            setErr(t('NOBALANCE'))
            setIsAutobet(false)
            return
        }

        if (lastGameResult.current === 'win') {
            // Обработчик увеличения ставки при выигрыше
            const newAmount = +(currentAmount + (currentAmount * +increaseOnWin / 100)).toFixed(initialState.currencyPrecision)
            if (+increaseOnWin > 0 && newAmount < initialState.limits.maxBet) {
                currentAmount = newAmount
                setAmount(newAmount.toString())

                // TODO сделать остановку, если нужно
            }

            // Обработчик стопа при лимите выигрыша
            if (+stopAutobetOnProfit > 0) {
                if (amountAutobetResult.current > +stopAutobetOnProfit) {
                    setErr(t('AUTOBETSSTOPPED'))
                    setIsAutobet(false)
                    return
                }
            }
        }

        if (lastGameResult.current === 'lose') {
            // Обработчик увеличения ставки при проигрыше
            const newAmount = +(currentAmount + (currentAmount * +increaseOnLoss / 100)).toFixed(initialState.currencyPrecision)
            if (+increaseOnLoss > 0 && newAmount < initialState.limits.maxBet) {
                currentAmount = newAmount
                setAmount(newAmount.toString())

                // TODO сделать остановку, если нужно
            }

            // Обработчик стопа при лимите проигрыша
            if (+stopAutobetOnLoss > 0) {
                if (amountAutobetResult.current * -1 > +stopAutobetOnLoss) {
                    setErr(t('AUTOBETSSTOPPED'))
                    setIsAutobet(false)
                    return
                }
            }
        }

        setIsAutobetRunning(true)

        setNumberOfRounds(prev => (+prev - 1).toString())

        await spin(currentAmount)
    }

    useEffect(() => {
        let timerId: NodeJS.Timeout | null = null

        if (isAutobet && !isGame && !!gameResult) {
            if (+numberOfRounds > 0) {
                timerId = setTimeout(() => runAutoBet(), 1000)
            } else {
                setIsAutobetRunning(false)
                setIsAutobet(false)
            }
        }

        return () => {
            if (timerId) {
                return clearTimeout(timerId)
            }
        }
    }, [isGame])

    const spin = async (amount: number) => {
        if (!connection) return

        if (amount > balance) {
            setErr(t('NOBALANCE'))
            return
        }

        if (amount > initialState.limits.maxBet || amount < initialState.limits.minBet) {
            return
        }

        setGameResult(null)
        setIsGame(true)

        betSound()

        setBalance(prev => prev - amount)

        await connection.invoke('Spin', { amount })
    }

    const changeResult = async () => {
        if (!connection) return

        const type = {
            riskType,
            sectorCount: segmentsCount,
        }

        await connection.invoke('ChangeResult', { type })
    }

    useEffect(() => {
        const connect = async () => {
            let token: string | null = null

            if (oneTimeToken) {
                try {
                    const { accessToken } = await Api().auth.login(oneTimeToken)
                    token = accessToken
                } catch (e) {
                    //
                }
            }

            let wsUrl = process.env.REACT_APP_WS as string

            if (token) {
                wsUrl = wsUrl + '?access_token=' + token
            }

            const c = new HubConnectionBuilder()
                .withUrl(wsUrl)
                .withAutomaticReconnect()
                .build()
            setConnection(c)
        }

        connect()
    }, [])

    useEffect(() => {
        if (!connection) return

        const runConnection = async () => {
            connection.on('InitialStateResult', handleInitialState)
            connection.on('ChangeResult', handleChangeResult)

            await connection.stop()
            await connection.start()

            let initStateDto: Object = { partnerId, gameName }

            if (mode === 'demo') {
                initStateDto = { ...initStateDto, isDemo: true }
            }

            await connection.invoke("InitialState", initStateDto )
        }
        runConnection()
    }, [connection])

    useEffect(() => {
        if (!connection) return

        connection.off('SpinResult')
        connection.on('SpinResult', handleSpinResult)
    }, [connection, initialState, riskType, segmentsCount, isMute, isTurboMode, isAutobet])

    if (!Object.keys(initialState).length) {
        return (
            <div className="h-screen flex items-center justify-center">
                <img
                    src={isDrizzlePartner ? '/drizzle-preloader.webp' : '/preloader.png'}
                    alt="Preloader"
                    className="w-[200px]"
                />
            </div>
        )
    }

    return (
        <div className={s.game}>
            <PlayingField
                riskType={riskType}
                segmentsCount={segmentsCount}
                gameResult={gameResult}
                isGame={isGame}
                isDrizzlePartner={isDrizzlePartner}
            />
            <Control
                isGame={isGame}
                amount={amount}
                setAmount={setAmount}
                riskType={riskType}
                setRiskType={setRiskType}
                segmentsCount={segmentsCount}
                setSegmentsCount={setSegmentsCount}

                isAutobet={isAutobet}
                setIsAutobet={setIsAutobet}
                numberOfRounds={numberOfRounds}
                increaseOnWin={increaseOnWin}
                setIncreaseOnWin={setIncreaseOnWin}
                increaseOnLoss={increaseOnLoss}
                setIncreaseOnLoss={setIncreaseOnLoss}
                setNumberOfRounds={setNumberOfRounds}
                stopAutobetOnProfit={stopAutobetOnProfit}
                setStopAutobetOnProfit={setStopAutobetOnProfit}
                stopAutobetOnLoss={stopAutobetOnLoss}
                setStopAutobetOnLoss={setStopAutobetOnLoss}
                runAutobet={runAutoBet}
                isAutobetRunning={isAutobetRunning}

                spin={spin}
            />

            {oldHashAndKey && (
                <Popup
                    active={popupOpen.provablyFairness}
                    setActive={value => openPopup('provablyFairness', value)}
                >
                    <ProvablyFairnessModal lastRound={oldHashAndKey} />
                </Popup>
            )}

            <Popup
                active={popupOpen.rules}
                setActive={value => openPopup('rules', value)}
            >
                <RulesPopup />
            </Popup>
        </div>
    )
}

export default Game

export type GameResult = '' | 'win' | 'lose'
